/**
 * MatchArtworkBottomSheet — Connect an unmatched income transaction to a portfolio piece
 *
 * Opens from the Spend view when tapping an unmatched income transaction.
 * Lets the user search, select an artwork, or mark the income as general (teaching, licensing).
 */

import { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { Verdict } from '@/types';
import { MEDIUMS } from '@/types';

interface MatchArtworkBottomSheetProps {
  open: boolean;
  onClose: () => void;
  transaction: {
    id: string;
    amount: number;
    merchant: string;
    date: string;
  };
  onMatch: (transactionId: string, artworkId: string) => void;
  onMarkGeneral: (transactionId: string) => void;
}

// Verdicts need to be passed in — we accept the full Verdict[] and extract what we need
interface MatchArtworkBottomSheetWithDataProps extends MatchArtworkBottomSheetProps {
  verdicts: Verdict[];
}

export function MatchArtworkBottomSheet({
  open,
  onClose,
  transaction,
  onMatch,
  onMarkGeneral,
  verdicts,
}: MatchArtworkBottomSheetWithDataProps) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Filter to unsold pieces, search on title + medium
  const pieces = useMemo(() => {
    const unsold = verdicts.filter(v => !v.sold);
    if (!search.trim()) return unsold;
    const q = search.trim().toLowerCase();
    return unsold.filter(v => {
      const title = (v.inputs.title || '').toLowerCase();
      const mediumLabel = (MEDIUMS[v.inputs.medium]?.label || v.inputs.medium).toLowerCase();
      return title.includes(q) || mediumLabel.includes(q);
    });
  }, [verdicts, search]);

  const selectedArtwork = useMemo(
    () => (selectedId ? verdicts.find(v => v.id === selectedId) : null),
    [selectedId, verdicts],
  );

  const handleMatch = useCallback(() => {
    if (!selectedId) return;
    onMatch(transaction.id, selectedId);
    onClose();
  }, [selectedId, transaction.id, onMatch, onClose]);

  const handleMarkGeneral = useCallback(() => {
    onMarkGeneral(transaction.id);
    onClose();
  }, [transaction.id, onMarkGeneral, onClose]);

  // Reset state when closing
  const handleClose = useCallback(() => {
    setSearch('');
    setSelectedId(null);
    onClose();
  }, [onClose]);

  if (!open) return null;

  const selectedTitle = selectedArtwork?.inputs.title || '';
  const ctaEnabled = !!selectedId;

  return createPortal(
    <>
      {/* Scrim */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(11,12,19,0.35)',
          zIndex: 200,
        }}
      />

      {/* Sheet */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#FFFFFF',
          borderRadius: '20px 20px 0 0',
          maxHeight: '78vh',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 201,
          animation: 'matchSheetIn 0.3s cubic-bezier(0.32, 0.72, 0, 1) forwards',
        }}
      >
        {/* Drag handle */}
        <div style={{ paddingTop: 10, paddingBottom: 2, flexShrink: 0 }}>
          <div
            style={{
              width: 36,
              height: 4,
              background: '#E8DDD3',
              borderRadius: 2,
              margin: '0 auto',
            }}
          />
        </div>

        {/* Header */}
        <div style={{ paddingTop: 10, paddingLeft: 20, paddingRight: 20 }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: '#0B0C13',
              lineHeight: 1.3,
            }}
          >
            Match to artwork
          </div>
        </div>

        {/* Transaction summary */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 10,
            padding: '0 20px 14px',
          }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              background: '#2C9C8C',
              flexShrink: 0,
            }}
          />
          <span style={{ fontWeight: 700, fontSize: 15, color: '#0B0C13' }}>
            ${Math.round(Math.abs(transaction.amount) / 100).toLocaleString()}
          </span>
          <span style={{ fontSize: 13, color: '#6B7280' }}>
            {transaction.merchant} · {transaction.date}
          </span>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: '#E8DDD3', margin: '0 20px' }} />

        {/* Search input */}
        <div style={{ padding: '14px 20px 8px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: '#F3EBE3',
              borderRadius: 12,
              padding: '10px 14px',
            }}
          >
            <svg
              width={16}
              height={16}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#9CA3AF"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx={11} cy={11} r={8} />
              <line x1={21} y1={21} x2={16.65} y2={16.65} />
            </svg>
            <input
              type="text"
              placeholder="Search portfolio..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 14,
                fontFamily: "'Manrope', 'JetBrains Mono', monospace",
                color: '#0B0C13',
              }}
            />
          </div>
        </div>

        {/* Section label */}
        <div
          style={{
            paddingTop: 6,
            paddingBottom: 4,
            paddingLeft: 20,
            paddingRight: 20,
            fontSize: 11,
            fontWeight: 700,
            color: '#6B7280',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          YOUR PORTFOLIO ({pieces.length})
        </div>

        {/* Scrollable piece list */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '0 12px 4px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {pieces.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '24px 20px',
                fontSize: 14,
                color: '#9CA3AF',
              }}
            >
              No pieces match &ldquo;{search}&rdquo;
            </div>
          ) : (
            pieces.map(v => {
              const title = v.inputs.title || `${v.inputs.width}×${v.inputs.height}"`;
              const mediumLabel = MEDIUMS[v.inputs.medium]?.label || v.inputs.medium;
              const dimensions = `${v.inputs.width}×${v.inputs.height}"`;
              const askingPrice = v.inputs.gutPrice
                ? `$${Math.round(v.inputs.gutPrice / 100).toLocaleString()}`
                : null;
              const isSelected = selectedId === v.id;
              const initial = (title[0] || '?').toUpperCase();

              return (
                <button
                  key={v.id}
                  onClick={() => setSelectedId(isSelected ? null : v.id)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: isSelected ? '1.5px solid #2C9C8C' : '1.5px solid transparent',
                    background: isSelected ? '#2C9C8C0C' : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {/* Initial badge */}
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: '#F3EBE3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 15,
                      fontWeight: 700,
                      color: '#0B0C13',
                      flexShrink: 0,
                    }}
                  >
                    {initial}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: '#0B0C13',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {title}
                    </div>
                    <div style={{ fontSize: 13, color: '#6B7280' }}>
                      {mediumLabel} · {dimensions}
                    </div>
                  </div>

                  {/* Asking price */}
                  {askingPrice && (
                    <div
                      style={{
                        fontFamily: '"Manrope", "JetBrains Mono", monospace',
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#0B0C13',
                        flexShrink: 0,
                      }}
                    >
                      {askingPrice}
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* "Not for a specific piece" link */}
        <div
          style={{
            paddingTop: 8,
            paddingBottom: 4,
            paddingLeft: 20,
            paddingRight: 20,
            textAlign: 'center',
          }}
        >
          <span
            onClick={handleMarkGeneral}
            style={{
              fontSize: 13,
              color: '#9CA3AF',
              textDecoration: 'underline',
              textUnderlineOffset: 2,
              cursor: 'pointer',
            }}
          >
            Not for a specific piece
          </span>
        </div>

        {/* CTA button */}
        <div style={{ padding: '8px 20px 28px' }}>
          <button
            disabled={!ctaEnabled}
            onClick={handleMatch}
            style={{
              width: '100%',
              padding: '15px 0',
              borderRadius: 12,
              border: 'none',
              background: ctaEnabled ? '#0B0C13' : '#E8DDD3',
              color: ctaEnabled ? '#FFFFFF' : '#6B7280',
              fontSize: 15,
              fontWeight: 700,
              fontFamily: "'Manrope', 'JetBrains Mono', monospace",
              cursor: ctaEnabled ? 'pointer' : 'default',
              transition: 'all 0.2s ease',
            }}
          >
            {ctaEnabled ? `Match to "${selectedTitle}"` : 'Select a piece'}
          </button>
        </div>
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes matchSheetIn {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>,
    document.body,
  );
}

export default MatchArtworkBottomSheet;
